import { createTheme, alpha } from '@mui/material/styles';

// Clean, editor-friendly theme
const cleanTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6366F1',
      light: '#818CF8',
      dark: '#4F46E5',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#8B5CF6',
      light: '#A78BFA',
      dark: '#7C3AED',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0F172A', // Revert to original app background
      paper: '#1E293B',   // Revert to original paper background
    },
    text: {
      primary: '#F8FAFC',   // Revert to original text
      secondary: '#CBD5E1', // Revert to original secondary
      disabled: '#64748B',  // Revert to original disabled
    },
    divider: alpha('#CBD5E1', 0.12), // Revert to original divider
    success: {
      main: '#10B981',
    },
    error: {
      main: '#EF4444',
    },
    warning: {
      main: '#F59E0B',
    },
    info: {
      main: '#3B82F6',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8, // Reduced from 16/24
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global layout - ONE source of truth
        html: {
          height: '100%',
          overflow: 'hidden', // Prevent body scroll
        },
        body: {
          backgroundColor: '#0F172A', // Revert to original app background
          margin: 0,
          padding: 0,
          height: '100%',
          overflow: 'hidden', // Prevent body scroll
        },
        '#root': {
          height: '100vh', // Exact browser height - no more, no less
          overflow: 'auto', // Allow scrolling within app
          display: 'flex',
          flexDirection: 'column',
        },
        '*': {
          boxSizing: 'border-box',
        },
        // Force zero spacing on main content areas
        'main': {
          margin: 0,
          padding: 0,
          '& > *:first-child': {
            marginTop: '0 !important',
          }
        },
        // Global reset for ALL pages to eliminate top spacing
        '[data-page]': {
          '& > *:first-child': {
            marginTop: '0 !important',
            paddingTop: '0 !important',
          }
        },
        // Special styling for docs and snippet pages - BIG writing canvas (Dark Mode)
        '[data-page="docs"], [data-page="create"], [data-page="snippets"]': {
          minHeight: '200vh', // Double browser height for long documents/code
          '& .editor-container': {
            minHeight: '200vh', // Ensure editor container is also tall
          },
          '& .editor-content': {
            minHeight: '180vh', // Big writing area
          },
          '& .snippet-editor': {
            minHeight: '180vh', // Big code writing area
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          background: '#6366F1',
          '&:hover': {
            background: '#4F46E5',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#1E293B',
            '& fieldset': {
              borderColor: alpha('#CBD5E1', 0.2),
            },
            '&:hover fieldset': {
              borderColor: alpha('#6366F1', 0.5),
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6366F1',
              borderWidth: 2,
            },
          },
          '& .MuiInputBase-input': {
            color: '#F8FAFC',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: '#1E293B',
          border: `1px solid ${alpha('#CBD5E1', 0.1)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1E293B',
          border: `1px solid ${alpha('#CBD5E1', 0.1)}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha('#CBD5E1', 0.2),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha('#6366F1', 0.5),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6366F1',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: alpha('#6366F1', 0.1),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha('#CBD5E1', 0.12),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#374151',
          color: '#F8FAFC',
          fontSize: '0.75rem',
          borderRadius: 6,
          border: `1px solid ${alpha('#CBD5E1', 0.1)}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          border: `1px solid ${alpha('#CBD5E1', 0.1)}`,
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha('#6366F1', 0.1),
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#6366F1', 0.2),
            '&:hover': {
              backgroundColor: alpha('#6366F1', 0.3),
            },
          },
        },
      },
    },
  },
});

// Light theme version with optimized text colors for readability
const cleanLightTheme = createTheme({
  ...cleanTheme,
  palette: {
    ...cleanTheme.palette,
    mode: 'light',
    primary: {
      main: '#6366F1', // Brand purple-blue (for buttons, icons, decorative elements)
      light: '#818CF8',
      dark: '#4F46E5', // Darker shade for better contrast when used as text
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF', // Pure white background
      paper: '#F8FAFC',   // Subtle off-white for cards/papers
    },
    text: {
      primary: '#1e293b',   // Dark charcoal gray for primary text (excellent contrast)
      secondary: '#667085', // Cool professional gray for secondary text
      disabled: '#94A3B8',  // Lighter gray for disabled states
    },
    divider: alpha('#667085', 0.12), // Using secondary text color for dividers
    // Custom colors for specific use cases
    action: {
      active: '#4F46E5', // Darker primary for text/links (your brand's dark variant)
      hover: alpha('#6366F1', 0.04),
      selected: alpha('#6366F1', 0.08),
      disabled: alpha('#667085', 0.26),
      disabledBackground: alpha('#667085', 0.12),
    },
  },
  components: {
    ...cleanTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        // Global layout - ONE source of truth (Light Mode)
        html: {
          height: '100%',
          overflow: 'hidden', // Prevent body scroll
        },
        body: {
          backgroundColor: '#FFFFFF', // Light mode background
          margin: 0,
          padding: 0,
          height: '100%',
          overflow: 'hidden', // Prevent body scroll
        },
        '#root': {
          height: '100vh', // Exact browser height - no more, no less
          overflow: 'auto', // Allow scrolling within app
          display: 'flex',
          flexDirection: 'column',
        },
        '*': {
          boxSizing: 'border-box',
        },
        // Force zero spacing on main content areas
        'main': {
          margin: 0,
          padding: 0,
          '& > *:first-child': {
            marginTop: '0 !important',
          }
        },
        // Global reset for ALL pages to eliminate top spacing
        '[data-page]': {
          '& > *:first-child': {
            marginTop: '0 !important',
            paddingTop: '0 !important',
          }
        },
        // Special styling for docs and snippet pages - BIG writing canvas (Light Mode)
        '[data-page="docs"], [data-page="create"], [data-page="snippets"]': {
          minHeight: '200vh', // Double browser height for long documents/code
          '& .editor-container': {
            minHeight: '200vh', // Ensure editor container is also tall
          },
          '& .editor-content': {
            minHeight: '180vh', // Big writing area
          },
          '& .snippet-editor': {
            minHeight: '180vh', // Big code writing area
          }
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: alpha('#475569', 0.2),
            },
            '&:hover fieldset': {
              borderColor: alpha('#6366F1', 0.5),
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6366F1',
              borderWidth: 2,
            },
          },
          '& .MuiInputBase-input': {
            color: '#0F172A',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: '#F8FAFC',
          border: `1px solid ${alpha('#475569', 0.1)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#F8FAFC',
          border: `1px solid ${alpha('#475569', 0.1)}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: `1px solid ${alpha('#667085', 0.1)}`, // Updated to use new secondary color
          borderRadius: 8,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    // Text field improvements for better visual separation
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F9FAFB', // Subtle gray background for better field definition
            '&:hover': {
              backgroundColor: '#F3F4F6',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB', // Subtle gray background
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    // Link styling with your brand's darker purple
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4F46E5', // Your brand's darker purple for better contrast
          textDecorationColor: alpha('#4F46E5', 0.4),
          '&:hover': {
            color: '#4338CA', // Even darker on hover
            textDecorationColor: '#4338CA',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#374151',
          color: '#F8FAFC',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
  },
});

export { cleanTheme as default, cleanLightTheme };
